import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { Button, Pill } from './primitives.jsx';
import Beacon from './Beacon.jsx';
import Menu from './Menu.jsx';
import Select from './Select.jsx';
import { ConfirmationDialog, useConfirmationDialog } from './ConfirmationDialog.jsx';
import { canManageCompanyInvitation, invitationCooldown, invitationDeliveryLabel, readInvitationAttempt, saveInvitationAttempt, clearInvitationAttempt, checkedInvitationCommand } from '../invitation-management.js';
import './CompanyMembers.css';

function invitationStorage() { try { return globalThis.localStorage; } catch { return null; } }

const ROLES = ['owner', 'admin', 'marketer', 'viewer'];
const ERRORS = {
  member_self_change_forbidden: 'Ask another company owner to change your access.',
  member_owner_change_forbidden: 'Only a company owner can change owner access.',
  member_management_forbidden: 'Your company role no longer allows member changes.',
  member_record_stale: 'This membership changed. Refresh the members before trying again.',
  member_not_found: 'This member is no longer available. Refresh the members.',
  member_account_inactive: 'A platform administrator must reactivate this account first.',
  platform_last_owner: 'Assign another active owner before changing this access.',
  invitation_management_forbidden: 'Your company role no longer allows invitation changes.',
  invitation_owner_required: 'Only a company owner can change this invitation.',
  invitation_recipient_unavailable: 'This recipient is unavailable. Contact a platform administrator.',
  invitation_not_found: 'This invitation is no longer available. Refresh the members.',
  invitation_not_pending: 'This invitation changed. Refresh the members.',
  invitation_version_conflict: 'This invitation changed. Refresh it before trying again.',
  invitation_action_conflict: 'This invitation action changed. Refresh the members before continuing.',
  invitation_role_unchanged: 'Choose a different role.',
  invitation_cooldown: 'Wait before requesting another email. Refresh to check when it is available.',
  invitation_rate_limited: 'Wait before requesting another email. Refresh to check when it is available.',
};

export function canManageCompanyMember(member, actorRole, actorId) {
  return ['owner', 'admin'].includes(actorRole) && Boolean(member?.updatedAt)
    && member.userId !== actorId && ['active', 'disabled'].includes(member.status)
    && (actorRole === 'owner' || member.role !== 'owner');
}

export default function CompanyMembers({ members, companyId, companyName, actorRole, actorId, onChanged, onEditStateChange }) {
  const [editing, setEditing] = useState(null);
  const [role, setRole] = useState('viewer');
  const [pending, setPending] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [attempt, setAttempt] = useState(() => readInvitationAttempt(invitationStorage(), companyId, actorId));
  const [now, setNow] = useState(Date.now);
  const confirmation = useConfirmationDialog();
  const scope = useRef(companyId);
  const currentActor = useRef(actorId);
  currentActor.current = actorId;
  const inFlight = useRef(false);
  scope.current = companyId;
  useEffect(() => { scope.current = companyId; return () => { scope.current = null; }; }, [companyId]);
  useEffect(() => { setAttempt(readInvitationAttempt(invitationStorage(), companyId, actorId)); setEditing(null); setFeedback(null); }, [companyId, actorId]);
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(timer); }, []);

  const editedMember = members.find(member => member.id === editing);
  const dirty = Boolean(editedMember && role !== editedMember.role);
  useEffect(() => {
    onEditStateChange?.({ dirty, busy: Boolean(pending) });
    return () => onEditStateChange?.({ dirty: false, busy: false });
  }, [dirty, pending, onEditStateChange]);

  async function runInvitationAttempt(command) {
    if (inFlight.current || scope.current !== command.companyId || currentActor.current !== command.actorId || !['owner', 'admin'].includes(actorRole)) return;
    inFlight.current = true;
    setPending({ id: command.invitationId, action: command.body.action }); setFeedback(null);
    const controller = new AbortController(); const timer = setTimeout(() => controller.abort(), 15000);
    try {
      saveInvitationAttempt(invitationStorage(), command);
      setAttempt(command);
      const result = checkedInvitationCommand(await api.post(
        `/api/saas/members/invitations/${encodeURIComponent(command.invitationId)}/manage`, command.body,
        { headers: { 'x-company-id': command.companyId }, signal: controller.signal },
      ), command);
      if (scope.current !== command.companyId || currentActor.current !== command.actorId) return;
      clearInvitationAttempt(invitationStorage(), command.companyId, command.actorId);
      setAttempt(null); setEditing(null);
      const uncertainDelivery = ['resend', 'recover'].includes(command.body.action) && (result.invitation.deliveryStatus === 'pending' || (result.invitation.deliveryStatus === 'failed' && result.invitation.failureCategory === 'unknown'));
      const message = uncertainDelivery ? 'Email delivery is unconfirmed. Check the invitation status before requesting another email.' : result.replayed === true ? 'Invitation action confirmed. Review its current status.' : command.body.action === 'role' ? 'Role updated. Send the new invitation when ready.'
        : command.body.action === 'cancel' ? 'Invitation canceled.'
          : result.email?.status === 'accepted' && result.invitation.deliveryStatus === 'recovery_sent' ? 'Reset link requested.'
            : result.email?.status === 'sent' && result.invitation.deliveryStatus === 'sent' ? 'Invitation sent.' : 'Invitation updated. Check its delivery status.';
      setFeedback({ tone: 'status', message }); onChanged?.();
    } catch (error) {
      if (scope.current !== command.companyId || currentActor.current !== command.actorId) return;
      const deterministic = Number(error.status) >= 400 && Number(error.status) < 500 && Number(error.status) !== 429;
      if (deterministic) { clearInvitationAttempt(invitationStorage(), command.companyId, command.actorId); setAttempt(null); }
      setFeedback({ tone: 'alert', message: error.message === 'invitation_continuation_unavailable'
        ? 'Allow site storage to safely continue this invitation action.'
        : ERRORS[error.code] || 'This invitation action could not be confirmed. Retry the saved action to check its result.' });
    } finally {
      clearTimeout(timer);
      inFlight.current = false;
      if (scope.current === command.companyId && currentActor.current === command.actorId) setPending(null);
    }
  }

  async function changeInvitation(member, action, nextRole = null) {
    if (inFlight.current || attempt || !canManageCompanyInvitation(member, actorRole, companyId)) return;
    if (['resend', 'recover'].includes(action) && invitationCooldown(member) > 0) return;
    const expectedCompany = companyId;
    if (action === 'cancel' || action === 'role') {
      const accepted = await confirmation.request({
        title: action === 'cancel' ? `Cancel the invitation to ${member.email}?` : `Change ${member.email} to ${nextRole}?`,
        description: action === 'cancel' ? `This invitation will no longer provide access to ${companyName}.`
          : 'The old invitation will stop working. Send the new invitation after saving this role.',
        confirmLabel: action === 'cancel' ? 'Cancel invitation' : 'Save role', tone: action === 'cancel' ? 'danger' : 'accent',
      });
      if (!accepted || scope.current !== expectedCompany || currentActor.current !== actorId || inFlight.current) return;
    }
    const command = { companyId: expectedCompany, actorId, invitationId: member.id,
      body: { action, expectedRevision: member.revision, actionKey: crypto.randomUUID(), ...(action === 'role' ? { role: nextRole } : {}) } };
    await runInvitationAttempt(command);
  }

  async function change(member, action, nextRole = null) {
    if (inFlight.current || attempt || !canManageCompanyMember(member, actorRole, actorId)) return;
    const expectedCompany = companyId;
    const label = member.displayName || member.email || 'this member';
    if (action !== 'role') {
      const accepted = await confirmation.request({
        title: `${action === 'remove' ? 'Remove' : action === 'disable' ? 'Disable' : 'Enable'} ${label}?`,
        description: action === 'remove'
          ? `Remove access to ${companyName}. Their other companies, account and activity history are kept. Returning requires a new invitation.`
          : action === 'disable'
            ? `Disable access to ${companyName}. You can enable this membership again later. Their other companies and activity history are kept.`
            : `Restore this membership's ${member.role} access to ${companyName}.`,
        confirmLabel: action === 'remove' ? 'Remove from company' : action === 'disable' ? 'Disable access' : 'Enable access',
        tone: action === 'enable' ? 'accent' : 'danger',
      });
      if (!accepted || scope.current !== expectedCompany || currentActor.current !== actorId || inFlight.current) return;
    }
    inFlight.current = true;
    setPending({ id: member.id, action });
    setFeedback(null);
    try {
      await api.patch(`/api/saas/members/${encodeURIComponent(member.id)}`, {
        action, expectedUpdatedAt: member.updatedAt, ...(nextRole ? { role: nextRole } : {}),
      }, { headers: { 'x-company-id': expectedCompany } });
      if (scope.current !== expectedCompany) return;
      setEditing(null);
      setFeedback({ message: 'Company access updated.', tone: 'status' });
      onChanged?.();
    } catch (error) {
      if (scope.current === expectedCompany) setFeedback({
        tone: 'alert', message: ERRORS[error.code] || 'The membership change could not be confirmed. Refresh members before trying again.',
      });
    } finally {
      inFlight.current = false;
      if (scope.current === expectedCompany) setPending(null);
    }
  }

  return <div className="bl-company-members" data-company-members-state="ready" aria-busy={Boolean(pending)}>
    {feedback && <p role={feedback.tone}>{feedback.message} {feedback.tone === 'alert' && <Button size="sm" variant="ghost" onClick={onChanged}>Refresh members</Button>}</p>}
    {attempt && <p role="status">An invitation action is awaiting confirmation. <Button size="sm" variant="ghost" disabled={Boolean(pending) || !['owner', 'admin'].includes(actorRole)} onClick={() => runInvitationAttempt(attempt)}>Retry saved action</Button></p>}
    {members.map((member) => {
      const name = member.displayName || member.email || 'Member';
      const manageable = canManageCompanyMember(member, actorRole, actorId);
      const invitationManageable = canManageCompanyInvitation(member, actorRole, companyId);
      const cooldown = invitationCooldown(member, now);
      const expiredInvitation = member.status === 'invited' && Date.parse(member.inviteExpiresAt) <= now;
      const locked = Boolean(pending || attempt);
      const isEditing = editing === member.id;
      return <article key={member.id} data-company-member-row className="bl-company-member" aria-label={name}>
        <div className="bl-company-member-identity">
          <span className="bl-company-member-avatar" aria-hidden="true">{name.split(/\s+/).map((part) => part[0]).slice(0, 2).join('').toUpperCase()}</span>
          <div><strong>{name}</strong>{member.email && member.email !== name && <span>{member.email}</span>}</div>
        </div>
        <div className="bl-company-member-controls">
          <Pill tone={member.role === 'owner' ? 'accent' : 'neutral'}>{member.role}</Pill>
          <Pill tone={member.status === 'active' ? 'live' : 'neutral'}>{expiredInvitation ? 'Expired' : member.status}</Pill>
          {member.status === 'invited' && member.deliveryStatus && <Pill tone={member.deliveryStatus === 'failed' ? 'warn' : 'neutral'}>{invitationDeliveryLabel(member)}</Pill>}
          {(manageable || invitationManageable) && <Menu label={`Manage ${name}`} triggerText="Manage" className="bl-member-menu" items={[
            { id: 'role', label: 'Update role', disabled: locked, onSelect: () => { setEditing(member.id); setRole(member.role); setFeedback(null); } },
            ...(invitationManageable ? [
              { id: 'resend', label: cooldown ? `Resend in ${cooldown}s` : member.sentAt ? 'Resend invitation' : 'Send invitation', disabled: locked || cooldown > 0, onSelect: () => changeInvitation(member, 'resend') },
              { id: 'recover', label: 'Send reset link', disabled: locked || cooldown > 0, onSelect: () => changeInvitation(member, 'recover') },
              { id: 'cancel', label: 'Cancel invitation', disabled: locked, onSelect: () => changeInvitation(member, 'cancel') },
            ] : [
              { id: 'access', label: member.status === 'disabled' ? 'Enable access' : 'Disable access', disabled: locked, onSelect: () => change(member, member.status === 'disabled' ? 'enable' : 'disable') },
              { id: 'remove', label: 'Remove from company', tone: 'danger', separatorBefore: true, disabled: locked, onSelect: () => change(member, 'remove') },
            ]),
          ]} />}
        </div>
        {isEditing && <form className="bl-member-role-form" onSubmit={(event) => { event.preventDefault(); if (invitationManageable) changeInvitation(member, 'role', role); else change(member, 'role', role); }}>
          <label>Company role<Select value={role} onChange={setRole} disabled={locked} autoFocus
            options={ROLES.filter((value) => actorRole === 'owner' || value !== 'owner').map((value) => ({ value, label: value[0].toUpperCase() + value.slice(1) }))} /></label>
          <Button type="submit" size="sm" variant="accent" disabled={locked || role === member.role}>Save role</Button>
          <Button type="button" size="sm" variant="ghost" disabled={locked} onClick={() => setEditing(null)}>Cancel</Button>
        </form>}
        <Beacon size="inline" active={pending?.id === member.id} slowAfterMs={null}
          label={pending?.action === 'remove' ? 'Removing company access…' : 'Saving company access…'} />
      </article>;
    })}
    <ConfirmationDialog {...confirmation.dialogProps} />
  </div>;
}
